import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth, isTTC, isCustomerAdmin } from '../hooks/useAuth';
import { useCart } from '../hooks/useCart';
import { useBranding } from '../hooks/useBranding';
import api from '../utils/api';
import Footer from './Footer';
import SecurityGates from './SecurityGates';
import {
  LayoutDashboard, ShoppingCart, ClipboardList, Package,
  Users, Building2, LogOut, Settings, ShieldCheck, MapPin, Palette, Layers, Mail, Upload, ListChecks,
  DollarSign
} from 'lucide-react';

export default function Layout() {
  const { user, logout, impersonating, impersonator, stopImpersonating } = useAuth();
  const { items } = useCart();
  const { ttcLogo, customerLogo, background, announcement } = useBranding();
  const [annDismissed, setAnnDismissed] = useState(false);
  const nav = useNavigate();

  const handleLogout = async () => {
    await logout();
    nav('/login');
  };

  // Background tile placement
  const tileOn = background?.enabled && background?.url;
  const tileStyle = tileOn
    ? { backgroundImage: `url("${background.url}")`, backgroundRepeat: 'repeat' }
    : undefined;
  const onToolbars = tileOn && (background.placement === 'toolbars' || background.placement === 'both');
  const onPage = tileOn && (background.placement === 'page' || background.placement === 'both');

  return (
    <div className="app-shell">
      <aside className="sidebar" style={onToolbars ? tileStyle : undefined}>
        <div className="sidebar-logo">
          {ttcLogo
            ? <img className="sidebar-logo-img" src={ttcLogo} alt="Toledo Ticket" />
            : (<><h1>Toledo Ticket</h1><p>Order Management</p></>)}
        </div>

        <nav className="sidebar-nav">
          <NavLink to="/" end className={({isActive}) => 'nav-link' + (isActive ? ' active' : '')}>
            <LayoutDashboard size={16} /> Dashboard
          </NavLink>

          <NavLink to="/orders" className={({isActive}) => 'nav-link' + (isActive ? ' active' : '')}>
            <ClipboardList size={16} /> Orders
          </NavLink>

          <NavLink to="/catalog" className={({isActive}) => 'nav-link' + (isActive ? ' active' : '')}>
            <Package size={16} /> Product Catalog
          </NavLink>

          <NavLink to="/cart" className={({isActive}) => 'nav-link' + (isActive ? ' active' : '')}>
            <ShoppingCart size={16} />
            Cart
            {items.length > 0 && <span className="cart-badge">{items.length}</span>}
          </NavLink>

          {isTTC(user) && (
            <>
              <div className="nav-section-label">Admin</div>
              <NavLink to="/admin/products" className={({isActive}) => 'nav-link' + (isActive ? ' active' : '')}>
                <Settings size={16} /> Item Manager
              </NavLink>
              <NavLink to="/admin/attributes" className={({isActive}) => 'nav-link' + (isActive ? ' active' : '')}>
                <ListChecks size={16} /> Question Builder
              </NavLink>
              <NavLink to="/admin/charges" className={({isActive}) => 'nav-link' + (isActive ? ' active' : '')}>
                <DollarSign size={16} /> Charge Catalog
              </NavLink>
              <NavLink to="/admin/imports" className={({isActive}) => 'nav-link' + (isActive ? ' active' : '')}>
                <Upload size={16} /> Imports
              </NavLink>
              <NavLink to="/admin/customers" className={({isActive}) => 'nav-link' + (isActive ? ' active' : '')}>
                <Building2 size={16} /> Customers
              </NavLink>
              <NavLink to="/admin/users" className={({isActive}) => 'nav-link' + (isActive ? ' active' : '')}>
                <Users size={16} /> Users
              </NavLink>
              <NavLink to="/admin/addresses" className={({isActive}) => 'nav-link' + (isActive ? ' active' : '')}>
                <MapPin size={16} /> Address Book
              </NavLink>
              <NavLink to="/admin/groups" className={({isActive}) => 'nav-link' + (isActive ? ' active' : '')}>
                <Layers size={16} /> Item Groups
              </NavLink>
              {user.role === 'ttc_superadmin' && (
                <>
                  <NavLink to="/admin/roles" className={({isActive}) => 'nav-link' + (isActive ? ' active' : '')}>
                    <ShieldCheck size={16} /> Role Manager
                  </NavLink>
                  <NavLink to="/admin/branding" className={({isActive}) => 'nav-link' + (isActive ? ' active' : '')}>
                    <Palette size={16} /> Branding
                  </NavLink>
                  <NavLink to="/admin/email" className={({isActive}) => 'nav-link' + (isActive ? ' active' : '')}>
                    <Mail size={16} /> Email Settings
                  </NavLink>
                  <NavLink to="/admin/settings" className={({isActive}) => 'nav-link' + (isActive ? ' active' : '')}>
                    <Settings size={16} /> Settings
                  </NavLink>
                  <NavLink to="/admin/audit" className={({isActive}) => 'nav-link' + (isActive ? ' active' : '')}>
                    <ShieldCheck size={16} /> Audit Log
                  </NavLink>
                </>
              )}
            </>
          )}

          {!isTTC(user) && isCustomerAdmin(user) && (
            <>
              <div className="nav-section-label">Admin</div>
              <NavLink to="/admin/users" className={({isActive}) => 'nav-link' + (isActive ? ' active' : '')}>
                <Users size={16} /> Users
              </NavLink>
              <NavLink to="/admin/addresses" className={({isActive}) => 'nav-link' + (isActive ? ' active' : '')}>
                <MapPin size={16} /> Address Book
              </NavLink>
              <NavLink to="/admin/groups" className={({isActive}) => 'nav-link' + (isActive ? ' active' : '')}>
                <Layers size={16} /> Item Groups
              </NavLink>
            </>
          )}
        </nav>

        <div className="sidebar-user">
          <strong>{user?.first_name} {user?.last_name}</strong>
          <span>{user?.email}</span>
          <br />
          <span style={{textTransform:'capitalize', fontSize:'11px', color:'rgba(255,255,255,0.35)'}}>
            {user?.role?.replace(/_/g,' ')}
          </span>
          <br />
          <button onClick={handleLogout}>
            <LogOut size={12} style={{display:'inline', marginRight:4}} />
            Sign Out
          </button>
        </div>
      </aside>

      <main className="main-content" style={onPage ? tileStyle : undefined}>
        {announcement?.enabled && announcement.text && !annDismissed && (
          <div style={{ background: 'var(--brand-gold, #FDDB32)', color: '#031E45', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, borderRadius: 8, margin: '0 0 14px', fontSize: 14 }}>
            <span>{announcement.text}</span>
            <button onClick={() => setAnnDismissed(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#031E45', fontSize: 18, lineHeight: 1 }} aria-label="Dismiss">×</button>
          </div>
        )}
        {impersonating && (
          <div className="impersonation-banner">
            <span>
              Viewing as <strong>{user?.first_name} {user?.last_name}</strong> ({user?.email})
              {impersonator && <> — you are <strong>{impersonator.first_name} {impersonator.last_name}</strong></>}
            </span>
            <button onClick={async () => {
              // Ends impersonation by logging out (returns to the login screen).
              try { await logout(); } catch (_) {}
              window.location.href = '/login';
            }}>
              Log out to end
            </button>
          </div>
        )}
        {customerLogo && (
          <div className="brandbar" style={onToolbars ? tileStyle : undefined}>
            <img className="brandbar-logo" src={customerLogo} alt="Customer logo" />
          </div>
        )}
        <Outlet />
        <Footer />
      </main>
      <SecurityGates />
    </div>
  );
}
